import { playArpeggio, playNoiseBurst, playTone, startAmbientDrone } from "./SoundSynth";

export type SfxName =
  | "footstep"
  | "attack"
  | "targetHit"
  | "targetDestroy"
  | "missionComplete"
  | "countdown"
  | "ceremonyTransition"
  | "prizeWin";

const SFX_FILES: Partial<Record<SfxName, string>> = {
  attack: "/audio/attack.mp3",
  targetHit: "/audio/target-hit.mp3",
  targetDestroy: "/audio/target-destroy.mp3",
  missionComplete: "/audio/mission-complete.mp3",
  countdown: "/audio/countdown.mp3",
  ceremonyTransition: "/audio/ceremony-transition.mp3",
  prizeWin: "/audio/prize-win.mp3",
  // "footstep" has no shipped file and isn't currently triggered anywhere;
  // it falls straight through to the procedural synth if ever used.
};

const AMBIENT_FILE = "/audio/ambient.mp3";

/**
 * Game audio bus. Real sound effects (royalty-free, Mixkit Free License —
 * see ASSETS.md) are preloaded and played from `/public/audio`; if a file
 * is ever missing or fails to decode, playback falls back to the procedural
 * synth in SoundSynth.ts so the game is never silent.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxBuffers = new Map<SfxName, AudioBuffer>();
  private ambientEl: HTMLAudioElement | null = null;
  private ambientSource: MediaElementAudioSourceNode | null = null;
  private ambientFallback: { stop: () => void } | null = null;
  private muted = false;

  /** Must be called from within a user-gesture handler (ENTER MISSION click). */
  async resumeContext(): Promise<void> {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.7;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      await this.ctx.resume().catch(() => undefined);
    }
    this.preloadSfx();
  }

  private preloadSfx(): void {
    if (!this.ctx) return;
    for (const [name, url] of Object.entries(SFX_FILES) as [SfxName, string][]) {
      if (this.sfxBuffers.has(name)) continue;
      fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${url} -> ${r.status}`))))
        .then((buf) => this.ctx!.decodeAudioData(buf))
        .then((decoded) => this.sfxBuffers.set(name, decoded))
        .catch(() => undefined); // falls back to procedural synth in play()
    }
  }

  play(name: SfxName, volume = 1): void {
    if (this.muted || !this.ctx || !this.masterGain) return;
    const buffer = this.sfxBuffers.get(name);
    if (buffer) {
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      const gain = this.ctx.createGain();
      gain.gain.value = volume;
      src.connect(gain).connect(this.masterGain);
      src.start();
      return;
    }
    this.playSynthFallback(name, volume);
  }

  private playSynthFallback(name: SfxName, volume: number): void {
    if (!this.ctx || !this.masterGain) return;
    const ctx = this.ctx;
    const dest = this.masterGain;
    switch (name) {
      case "footstep":
        playNoiseBurst(ctx, dest, { duration: 0.05, filterStart: 300, filterEnd: 120, volume: 0.12 * volume });
        break;
      case "attack":
        playTone(ctx, dest, { type: "sawtooth", startFreq: 900, endFreq: 220, duration: 0.09, volume: 0.35 * volume });
        break;
      case "targetHit":
        playTone(ctx, dest, { type: "square", startFreq: 1200, duration: 0.05, volume: 0.3 * volume });
        break;
      case "targetDestroy":
        playNoiseBurst(ctx, dest, { duration: 0.5, filterStart: 2200, filterEnd: 90, volume: 0.55 * volume });
        playTone(ctx, dest, { type: "sawtooth", startFreq: 500, endFreq: 60, duration: 0.45, volume: 0.3 * volume });
        break;
      case "missionComplete":
        playArpeggio(ctx, dest, [523.25, 659.25, 783.99, 1046.5], 0.16, "triangle");
        break;
      case "countdown":
        playTone(ctx, dest, { type: "sine", startFreq: 880, duration: 0.13, volume: 0.4 * volume });
        break;
      case "ceremonyTransition":
        playNoiseBurst(ctx, dest, { duration: 1.1, filterStart: 150, filterEnd: 2500, volume: 0.35 * volume });
        playTone(ctx, dest, { type: "sine", startFreq: 150, endFreq: 900, duration: 1.1, volume: 0.25 * volume });
        break;
      case "prizeWin":
        // Longer/brighter than missionComplete's stinger — a little fanfare for the lucky-draw alert.
        playArpeggio(ctx, dest, [523.25, 523.25, 659.25, 783.99, 659.25, 783.99, 1046.5, 1318.51], 0.14, "square");
        break;
    }
  }

  startAmbient(): void {
    if (this.ambientEl || this.ambientFallback || !this.ctx || !this.masterGain) return;
    const audio = new Audio(AMBIENT_FILE);
    audio.loop = true;
    audio.volume = 1;
    audio.crossOrigin = "anonymous";

    const useFallback = () => {
      if (this.ambientEl) {
        this.ambientEl.pause();
        this.ambientEl = null;
      }
      if (!this.ambientFallback && this.ctx && this.masterGain) {
        this.ambientFallback = startAmbientDrone(this.ctx, this.masterGain, 0.16);
      }
    };

    audio
      .play()
      .then(() => {
        try {
          this.ambientSource = this.ctx!.createMediaElementSource(audio);
          this.ambientSource.connect(this.masterGain!);
        } catch {
          /* CORS/decoding issue — audio still plays through the element directly at full volume */
        }
        this.ambientEl = audio;
      })
      .catch(useFallback);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain) this.masterGain.gain.value = muted ? 0 : 0.7;
    if (this.ambientEl) this.ambientEl.muted = muted;
  }

  setMasterVolume(v: number): void {
    if (this.masterGain) this.masterGain.gain.value = Math.max(0, Math.min(1, v));
  }

  /** Fades game audio down then stops it, so it never collides with the ceremony video's sound. */
  fadeOutAndStop(durationMs = 900): void {
    const start = this.masterGain?.gain.value ?? 0.7;
    const startTime = performance.now();
    const step = () => {
      const t = Math.min(1, (performance.now() - startTime) / durationMs);
      if (this.masterGain) this.masterGain.gain.value = start * (1 - t);
      if (t < 1) requestAnimationFrame(step);
      else this.stopAll();
    };
    requestAnimationFrame(step);
  }

  stopAll(): void {
    if (this.ambientEl) {
      this.ambientEl.pause();
      this.ambientEl = null;
    }
    this.ambientFallback?.stop();
    this.ambientFallback = null;
  }
}
